import React, { useEffect, useRef } from "react";
import { User } from 'lucide-react';
import c from "classnames";
import "./audio-pulse1.scss";

export type AudioPulseProps = {
  active: boolean;
  volume: number;
  hover?: boolean;
};

export default function AudioPulse1({ 
  active, 
  volume, 
  hover = false 
}: AudioPulseProps) {
  const rings = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    let timeout: number | null = null;
    const update = () => {
      rings.current.forEach((ring, i) => {
        if (!ring) return;
        
        // Adjust animation based on volume
        const volumeMultiplier = Math.max(0.2, Math.min(1, volume * 2)); // Minimum scale of 0.2
        const baseScale = 1 + (i * 0.2); // Different base scale for each ring
        const maxScale = baseScale + (1.5 * volumeMultiplier);
        
        // Update animation properties
        ring.style.animation = 'none'; // Remove default animation
        ring.style.opacity = `${0.6 * (1 - (i * 0.2)) * volumeMultiplier}`;
        ring.style.transform = `scale(${maxScale})`;
        ring.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';
      });
      
      timeout = window.setTimeout(update, 50); // Faster updates for smoother animation
    };

    if (active) {
      update();
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [volume, active]);

  return (
    <div className={c("audioPulse", { active, hover })}>
      {active && (
        <>
          <div 
            ref={(el) => rings.current[0] = el!} 
            className="pulse-ring"
          />
          <div 
            ref={(el) => rings.current[1] = el!} 
            className="pulse-ring"
          />
          <div 
            ref={(el) => rings.current[2] = el!} 
            className="pulse-ring"
          />
        </>
      )}
      <div className="audioPulse-icon">
        <User size={24} strokeWidth={1.5} />
      </div>
    </div>
  );
}